if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartNotification) {
          cartNotification.setActiveElement(this.openedBy);
        }

        this.modalContent.innerHTML = '';

        if (preventFocus) {
          this.openedBy = null;
        }

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading-overlay__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Quick View request failed: ${response.status}`);
            }

            return response.text();
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );

            if (!this.productElement) {
              throw new Error('Main product section was not found.');
            }

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            this.setInnerHTML(
              this.modalContent,
              this.productElement.innerHTML
            );

            /*
             * Nebü uses two product media wrappers when the product gallery
             * is positioned on the right. This selects the real media-gallery,
             * removes the hidden duplicate and places the visible gallery
             * before the product information inside Quick View.
             */
            this.createProductPreview(opener);
            this.initializeCustomOptionCards(opener);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            super.show(opener);
          })
          .catch((error) => {
            console.error('Quick View error:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        /*
         * Scripts inserted through innerHTML do not execute automatically.
         * Recreating them allows the product and variant scripts to initialize.
         */
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');

          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });

          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );

          oldScriptTag.parentNode.replaceChild(
            newScriptTag,
            oldScriptTag
          );
        });
      }

     createProductPreview(opener) {
  const product = this.modalContent.querySelector('.product');

  if (!product) return;

  const productInfo = product.querySelector(
    '.product__info-wrapper'
  );

  /*
   * First, obtain the product image from the fetched product HTML.
   * Nebü's custom desktop slider contains a normal image with
   * the full-resolution Shopify URL.
   */
  let sourceImage = Array.from(
    product.querySelectorAll('.product__media-wrapper img')
  ).find((image) => {
    return (
      image.getAttribute('src') ||
      image.getAttribute('srcset') ||
      image.dataset.src
    );
  });

  /*
   * If the fetched product gallery does not provide an image,
   * use the visible image from the recommendation card.
   */
  if (!sourceImage && opener) {
    const productCard =
      opener.closest('.card-wrapper') ||
      opener.closest('.card');

    if (productCard) {
      sourceImage = productCard.querySelector(
        '.card__media img, .media img, img'
      );
    }
  }

  /*
   * Remove both of the PDP media wrappers. They depend on the
   * custom product slider and cannot initialize correctly inside
   * Shopify's dynamically loaded Quick View.
   */
  Array.from(product.children)
    .filter((element) => {
      return (
        element.classList &&
        element.classList.contains('product__media-wrapper')
      );
    })
    .forEach((wrapper) => wrapper.remove());

  if (!sourceImage || !productInfo) return;

  const imageSource =
    sourceImage.getAttribute('src') ||
    sourceImage.dataset.src ||
    sourceImage.currentSrc;

  if (!imageSource) return;

  const previewContainer = document.createElement('div');

  previewContainer.className =
    'quick-add-modal__nebu-media grid__item';

  const previewImage = document.createElement('img');

  previewImage.className = 'quick-add-modal__nebu-image';
  previewImage.src = imageSource;
  previewImage.alt =
    sourceImage.getAttribute('alt') || 'Product image';
  previewImage.loading = 'eager';
  previewImage.decoding = 'async';

  const imageSrcset =
    sourceImage.getAttribute('srcset') ||
    sourceImage.dataset.srcset;

  if (imageSrcset) {
    previewImage.setAttribute('srcset', imageSrcset);
    previewImage.setAttribute(
      'sizes',
      '(min-width: 750px) 45vw, calc(100vw - 6rem)'
    );
  }

  previewContainer.appendChild(previewImage);
  product.insertBefore(previewContainer, productInfo);
}
async initializeCustomOptionCards(opener) {
  const customOptions = Array.from(
    this.modalContent.querySelectorAll(
      '.custom-option-card input[type="radio"]'
    )
  );

  const nativeSelects = Array.from(
    this.modalContent.querySelectorAll(
      'variant-selects select'
    )
  );

  if (!customOptions.length || !nativeSelects.length) return;

  let variants = [];

  /*
   * Load complete product data so price, weight,
   * availability and selected variant can update immediately.
   */
  try {
    const productURL = new URL(
      opener.getAttribute('data-product-url'),
      window.location.origin
    );

    productURL.search = '';
    productURL.pathname =
      productURL.pathname
        .replace(/\.js$/, '')
        .replace(/\/$/, '') + '.js';

    const response = await fetch(productURL.toString(), {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Product information request failed: ${response.status}`
      );
    }

    const productData = await response.json();
    variants = productData.variants || [];
  } catch (error) {
    console.error('Quick View variant data error:', error);
  }

  const getSelectedVariant = () => {
    if (!variants.length) return null;

    const selectedOptions = nativeSelects.map(
      (select) => select.value
    );

    return (
      variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;

        return variant.options.every((option, index) => {
          return String(option) === String(selectedOptions[index]);
        });
      }) || null
    );
  };

  const getWeightInGrams = (variant) => {
    if (!variant) return 0;

    if (
      variant.grams !== undefined &&
      variant.grams !== null &&
      !Number.isNaN(Number(variant.grams))
    ) {
      return Number(variant.grams);
    }

    let weight = Number(variant.weight) || 0;
    const unit = String(
      variant.weight_unit || 'g'
    ).toLowerCase();

    if (unit === 'kg') {
      weight *= 1000;
    } else if (unit === 'oz') {
      weight *= 28.349523125;
    } else if (unit === 'lb') {
      weight *= 453.59237;
    }

    return weight;
  };

  const formatGrams = (grams) => {
    const rounded = Math.round(grams * 100) / 100;

    return Number.isInteger(rounded)
      ? String(rounded)
      : String(rounded.toFixed(2)).replace(/\.?0+$/, '');
  };

  const formatPrice = (priceInCents) => {
    const currency =
      window.Shopify?.currency?.active || 'USD';

    const locale =
      document.documentElement.lang || 'en-US';

    try {
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(priceInCents) / 100);

      return `${formatted} ${currency}`;
    } catch (error) {
      return `$${(Number(priceInCents) / 100).toFixed(2)} ${currency}`;
    }
  };

  const updateVariantInformation = () => {
    const selectedVariant = getSelectedVariant();

    if (!selectedVariant) return;

    const grams = getWeightInGrams(selectedVariant);

    const troyOunces =
      Math.floor((grams / 31.1034768) * 10000) / 10000;

    /*
     * Update grams highlight.
     */
    const gramsElement = this.modalContent.querySelector(
      'h4[id^="dynamic-product-weight-"]:not([id*="-oz-"])'
    );

    if (gramsElement) {
      gramsElement.textContent =
        `${formatGrams(grams)} g`;
    }

    /*
     * Update troy ounces highlight.
     */
    const ouncesElement = this.modalContent.querySelector(
      'h4[id^="dynamic-product-weight-oz-"]'
    );

    if (ouncesElement) {
      ouncesElement.textContent =
        `${troyOunces} oz`;
    }

    /*
     * Update every product form with the selected variant.
     * This includes Add to cart and Shop Pay.
     */
    this.modalContent
      .querySelectorAll(
        'form[action*="/cart/add"] input[name="id"]'
      )
      .forEach((variantInput) => {
        variantInput.value = selectedVariant.id;

        variantInput.dispatchEvent(
          new Event('change', {
            bubbles: true
          })
        );
      });

    /*
     * Update displayed product price immediately.
     */
    const priceContainer = this.modalContent.querySelector(
      '[id^="price-"]'
    );

    if (priceContainer) {
      const regularPrice =
        priceContainer.querySelector(
          '.price__regular .price-item--regular'
        );

      const salePrice =
        priceContainer.querySelector(
          '.price__sale .price-item--sale'
        );

      const comparePrice =
        priceContainer.querySelector(
          '.price__sale .price-item--regular'
        );

      const formattedPrice = formatPrice(
        selectedVariant.price
      );

      const hasDiscount =
        selectedVariant.compare_at_price &&
        Number(selectedVariant.compare_at_price) >
          Number(selectedVariant.price);

      if (hasDiscount) {
        priceContainer
          .querySelector('.price')
          ?.classList.add('price--on-sale');

        if (salePrice) {
          salePrice.textContent = formattedPrice;
        }

        if (comparePrice) {
          comparePrice.textContent = formatPrice(
            selectedVariant.compare_at_price
          );
        }
      } else {
        priceContainer
          .querySelector('.price')
          ?.classList.remove('price--on-sale');

        if (regularPrice) {
          regularPrice.textContent = formattedPrice;
        }

        if (salePrice) {
          salePrice.textContent = formattedPrice;
        }
      }
    }

    /*
     * Update Add to cart availability.
     */
    const addButton = this.modalContent.querySelector(
      '.product-form__submit'
    );

    if (addButton) {
      const buttonText = addButton.querySelector('span');

      addButton.disabled = !selectedVariant.available;

      if (buttonText) {
        buttonText.textContent = selectedVariant.available
          ? 'Add to cart'
          : 'Sold out';
      }
    }
  };

  customOptions.forEach((radio) => {
    radio.addEventListener('change', () => {
      const optionPosition =
        Number.parseInt(
          radio.dataset.optionPosition,
          10
        ) - 1;

      const targetSelect =
        nativeSelects[optionPosition];

      if (!targetSelect || optionPosition < 0) return;

      targetSelect.value = radio.value;

      targetSelect.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );

      /*
       * Update immediately, then once more after Shopify
       * completes its native variant refresh.
       */
      updateVariantInformation();

      window.setTimeout(
        updateVariantInformation,
        150
      );

      window.setTimeout(
        updateVariantInformation,
        500
      );
    });
  });

  /*
   * Synchronize the initially selected variant.
   */
  updateVariantInformation();
}
      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector(
          'variant-radios, variant-selects'
        );

        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability =
          this.productElement.querySelector('pickup-availability');

        if (pickupAvailability) {
          pickupAvailability.remove();
        }

        const productModal =
          this.productElement.querySelector('product-modal');

        if (productModal) {
          productModal.remove();
        }

        this.productElement
          .querySelectorAll('modal-dialog')
          .forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;

        if (!sectionId) return;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );

        this.productElement
          .querySelectorAll(
            'variant-selects, variant-radios, product-info'
          )
          .forEach((element) => {
            element.dataset.originalSection = sectionId;
          });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector(
          '[id^="Slider-Gallery"]'
        );

        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');

        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((item) =>
            item.setAttribute('role', 'presentation')
          );
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');

        if (!product) return;

        const mediaImages = product.querySelectorAll(
          '.product__media img'
        );

        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, ' +
          '(min-width: 750px) calc((100vw - 11.5rem) / 2), ' +
          'calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace(
            '715px',
            '605px'
          );
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace(
            '715px',
            '495px'
          );
        }

        mediaImages.forEach((image) => {
          image.setAttribute('sizes', mediaImageSizes);

          /*
           * The first Quick View image should load immediately.
           */
          image.removeAttribute('loading');
          image.setAttribute('loading', 'eager');
        });
      }
    }
  );
}